import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_TENANTS } from '../../routes/paths';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const TenantCreationGuide: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const steps = [
    {
      title: 'Step 1: Download the PowerShell Script',
      description: (
        <>
          <h2 className="text-lg font-bold mb-2">Prerequisites:</h2>
          <ul className="list-disc list-inside mb-6">
            <li>Global Admin in target tenant or sufficient privileges to create a registered app.</li>
            <li>Key Vault Admin in the MVW tenant (MVW-Internal Subscription) or sufficient privileges to register a Secret.</li>
          </ul>
          <p className="mb-4">
            Download the PowerShell script from the provided source by clicking{' '}
            <a
              href="https://mvwtech.sharepoint.com/sites/ArchitectureandDesign/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FArchitectureandDesign%2FShared%20Documents%2FGeneral%2F07%20Script%20Repository%2F01%20Microsoft%20365%2FM365%20Tenant%20Configurator%2FRegister%2DMVWSocReporterApp%2Eps1&parent=%2Fsites%2FArchitectureandDesign%2FShared%20Documents%2FGeneral%2F07%20Script%20Repository%2F01%20Microsoft%20365%2FM365%20Tenant%20Configurator&p=true&ga=1"
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 underline"
            >
              here
            </a>
            .
          </p>
          <p>
            Ensure that you have permissions to run scripts on your system. You may need to adjust the
            execution policy if you encounter errors.
          </p>
        </>
      ),
      images: ['/images/tenants/tenant-creation-guide-1.png'],
    },
    {
      title: 'Step 2: Run the PowerShell Script',
      description: (
        <>
          Run the script manually using the following command:
          <div className="bg-gray-100 border border-gray-300 rounded-md p-4 mt-4 overflow-x-auto relative">
            <pre className="text-sm font-mono text-gray-800 whitespace-pre-wrap" id="script-command">
              {`.\\Register-MVWSocReporterApp.ps1 -OrgId "<OrgId>" -tenantId "<TenantID from target tenant>" -userUPN "<UPN OF ADMIN ACCOUNT WITH GA ROLE ASSIGNED>"`}
            </pre>
            <button
              onClick={() => {
                const command = `.\\Register-MVWSocReporterApp.ps1 -OrgId "<OrgId>" -tenantId "<TenantID from target tenant>" -userUPN "<UPN OF ADMIN ACCOUNT WITH GA ROLE ASSIGNED>"`;
                navigator.clipboard.writeText(command)
                  .then(() => {
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 2000);
                  })
                  .catch(err => {
                    console.error('Error al copiar el comando: ', err);
                  });
              }}
              className="absolute top-2 right-2 bg-gray-400 text-white p-1 rounded text-xs flex items-center justify-center"
            >
              <ContentCopyIcon fontSize="small" />
            </button>
            
            {copied && (
              <div className="absolute top-2 right-10 bg-black text-white px-2 py-1 rounded text-sm transition-opacity duration-300 whitespace-nowrap">
                Copied to clipboard
              </div>
            )}
          </div>
          <p className="mt-4">
            When prompted, provide the following information:
          </p>
          <ul className="list-disc list-inside">
            <li>OrgId (3 letter identifier e.g. MVW) of target tenant</li>
            <li>Admin User Email</li>
            <li>Tenant ID</li>
          </ul>
          <p>
            The script will create the necessary resources. This process might take up to 10 minutes.
          </p>
        </>
      ),
      images: [
        '/images/tenants/tenant-creation-guide-2a.png',
        '/images/tenants/tenant-creation-guide-2b.png',
        '/images/tenants/tenant-creation-guide-2c.png'
      ],
    },
    {
      title: 'Step 3: Verify the Application',
      description:
        'After the script finishes execution, verify that the application has been created successfully in Azure. You can do this by navigating to your Azure portal and checking the resources.',
      images: ['/images/tenants/tenant-creation-guide-3a.png', '/images/tenants/tenant-creation-guide-3b.png'],
    },
    {
      title: 'Step 4: Manually Create the Tenant',
      description: (
        <>
          Once the application is verified, create the tenant manually in the program. Use the admin
          panel or the dedicated interface to configure tenant details as needed. <br />
          <br />
          To proceed, click{' '}
          <button
            onClick={() => navigate(PATH_TENANTS.general.create)}
            className="text-blue-500 underline cursor-pointer"
          >
            here
          </button>{' '}
          to go to the tenant creation page.
        </>
      ),
      images: ['/images/tenants/tenant-creation-guide-4a.png'],
    },
  ];

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isModalOpen]);

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % steps[currentStep].images.length);
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? steps[currentStep].images.length - 1 : prevIndex - 1
    );
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setCurrentImageIndex(0);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setCurrentImageIndex(0);
    }
  };

  const openModal = (index: number) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="container mx-auto p-6 relative">
      <h1 className="text-2xl font-bold mb-6 text-center">Tenant Onboarding Guide</h1>

      <div className="bg-white shadow-md rounded-lg p-6 mb-4">
        <h2 className="text-xl font-semibold mb-4 text-center">
          {steps[currentStep].title}
        </h2>
        <p className="text-gray-700 mb-4 whitespace-pre-wrap">
          {steps[currentStep].description}
        </p>
        <div className="flex items-center justify-center relative">
          {steps[currentStep].images.length > 1 && (
            <button
              className="absolute left-0 text-red-800 font-bold p-2 z-10"
              style={{
                fontSize: '2rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => setCurrentImageIndex((prevIndex) =>
                prevIndex === 0 ? steps[currentStep].images.length - 1 : prevIndex - 1
              )}
            >
              ◀
            </button>
          )}

          <img
            src={steps[currentStep].images[currentImageIndex]}
            alt={`Step ${currentStep + 1} - ${currentImageIndex + 1}`}
            className="max-w-full max-h-48 cursor-pointer"
            onClick={() => openModal(currentImageIndex)}
          />

          {steps[currentStep].images.length > 1 && (
            <button
              className="absolute right-0 text-red-800 font-bold p-2 z-10"
              style={{
                fontSize: '2rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => setCurrentImageIndex((prevIndex) =>
                (prevIndex + 1) % steps[currentStep].images.length
              )}
            >
              ▶
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={handlePreviousStep}
          disabled={currentStep === 0}
          className={`px-4 py-2 font-bold rounded ${
            currentStep === 0 ? 'bg-gray-300' : 'bg-red-800 text-white'
          }`}
        >
          Previous
        </button>
        <button
          onClick={handleNextStep}
          disabled={currentStep === steps.length - 1}
          className={`px-4 py-2 font-bold rounded ${
            currentStep === steps.length - 1 ? 'bg-gray-300' : 'bg-red-800 text-white'
          }`}
        >
          Next
        </button>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          style={{ zIndex: 9999 }}
        >
          <div
            className="relative w-full h-full flex items-center justify-center"
            onClick={closeModal}
          >
            <button
              className="absolute left-3 text-red-800 font-bold p-2"
              style={{
                fontSize: '2rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                handlePreviousImage();
              }}
            >
              ◀
            </button>

            <img
              src={steps[currentStep].images[currentImageIndex]}
              alt="Enlarged"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            <button
              className="absolute right-3 text-red-800 font-bold p-2"
              style={{
                fontSize: '2rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
            >
              ▶
            </button>

            <button
              className="absolute top-3 right-3 text-white font-bold bg-gray-800 rounded-full p-2"
              onClick={closeModal}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantCreationGuide;
